import React, { useEffect, useState } from 'react';

import { NextPageWithLayout } from 'next';
import { useRouter } from 'next/navigation';

import { Alert } from '../../components/Alert/Alert';
import { Heading } from '../../components/Heading/Heading';
import { MetaTags } from '../../components/MetaTags/MetaTags';
import { ProPaymentPlans } from '../../components/ProPaymentPlans/ProPaymentPlans';
import { Redirect } from '../../components/Redirect/Redirect';
import { StickyBannerGetPro } from '../../components/StickyBottomBanner/StickyBannerGetPro';
import { FREE_TRIAL_DAYS, PRIMARY_OFFER_ID, type ProPlanId } from '../../constants/premium';
import { useAnalytics } from '../../contexts/AnalyticsContext/AnalyticsContext';
import { trackEvent } from '../../helpers/analytics/trackEvent';
import { reportMykissError } from '../../helpers/errorHandling';
import { useClientHasRendered } from '../../helpers/hooks';
import { useCoupon } from '../../helpers/hooks/useCoupon';
import { useCurrentURL } from '../../helpers/hooks/useCurrentURL/useCurrentURL';
import { useDistanceFromTop } from '../../helpers/hooks/useDistanceFromTop';
import { useIsMobileDevice } from '../../helpers/hooks/useIsMobileDevice';
import { useScreenHeight } from '../../helpers/hooks/useScreenHeight';
import { useUserInfo } from '../../helpers/hooks/useUserInfo';
import { LoadingPage } from '../LoadingPage/LoadingPage';
import {
  URLParameters,
  companiesURLParameters,
  flowsURLParameters,
} from '../Onboarding/constants/url';

import wave1 from './assets/wave1.svg';
import { ProFeaturesList } from './components/ProFeatureList';
import { ProSections } from './components/ProSections';

export interface IProLandingPageProps {
  couponCode?: string;
  canonicalPath: string;
}

export const ProLandingPage: NextPageWithLayout<IProLandingPageProps> = ({
  couponCode,
  canonicalPath,
}) => {
  const { isPremium, isLoadingUser, premiumEndDate } = useUserInfo();
  const { coupon, isLoading: couponIsLoading, codeIsInvalid } = useCoupon(couponCode);
  const clientHasRendered = useClientHasRendered();
  const isMobileDevice = useIsMobileDevice();
  const [proPlanId, setProPlanId] = useState(PRIMARY_OFFER_ID);
  const [userHasBeenPro, setUserHasBeenPro] = useState(false);
  const router = useRouter();
  const [showInvalidCodeError, setShowInvalidCodeError] = useState<boolean>(false);
  const distanceFromTop = useDistanceFromTop();
  const screenHeight = useScreenHeight();
  const { currentPage } = useAnalytics();
  const { addParameters, hasParameters } = useCurrentURL();
  const submitLink = addParameters({
    [URLParameters.Company]: companiesURLParameters.fishbrain,
    [URLParameters.Flow]: flowsURLParameters.fishbrain.becomePro,
  });
  const hasOnboardingUrlParams = hasParameters([URLParameters.Company, URLParameters.Flow]);

  const handleAlertClose = (): void => {
    setShowInvalidCodeError(false);
    try {
      router.push('/pro');
    } catch (error) {
      reportMykissError(error);
    }
  };

  const handleSubmitLinkClick = (): void => {
    sessionStorage.setItem('subscription.plan.id', JSON.stringify(proPlanId));
    void trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        type: 'pro',
        source: 'pro-payment-plan',
        page: currentPage,
      },
    });
  };

  useEffect(() => {
    if (premiumEndDate) {
      const currentDate = new Date();
      const result = premiumEndDate.localeCompare(currentDate.toString());
      setUserHasBeenPro(result <= 0);
    }
  }, [premiumEndDate]);

  useEffect(() => {
    const handleSessionStorageChange = (): void => {
      const planIdFromStorage = sessionStorage.getItem('subscription.plan.id');
      setProPlanId(
        (planIdFromStorage && (JSON.parse(planIdFromStorage) as ProPlanId)) || PRIMARY_OFFER_ID,
      );
    };

    window.addEventListener('planInStorageChanged', handleSessionStorageChange);

    handleSessionStorageChange();

    return () => {
      window.removeEventListener('planInStorageChanged', handleSessionStorageChange);
    };
  }, []);

  useEffect(() => {
    if (coupon) {
      setProPlanId(coupon.group.stripe_plan.external_id);
    }
  }, [coupon]);

  useEffect(() => {
    if (codeIsInvalid) {
      setShowInvalidCodeError(true);
    }
  }, [codeIsInvalid]);

  if (couponCode && codeIsInvalid) {
    return <Redirect to="/pro" />;
  }

  if (isPremium && !hasOnboardingUrlParams) {
    return <Redirect to="/pro/overview" />;
  }

  return (
    <>
      <MetaTags
        title="Fish smarter with Fishbrain Pro"
        ogTitle={couponCode && 'Claim your Fishbrain Pro offer'}
        description="Fishbrain pro helps you fish smarter today. Check out features like depth maps, catch logging, fising regulations and much more"
        link={canonicalPath}
      />
      {!clientHasRendered || isLoadingUser ? (
        <LoadingPage />
      ) : (
        <>
          {showInvalidCodeError && (
            <Alert
              bodyCopy="The coupon code is not valid anymore"
              alertShade="pinktail"
              onInteraction={handleAlertClose}
            />
          )}

          <header className="flex flex-col justify-center bg-lake-fog md:flex-row md:px-4 md:py-14">
            <div className="col-start-1 row-start-1 mx-6 mb-3 mt-6 md:pr-16">
              {!couponIsLoading && (
                <>
                  {userHasBeenPro || coupon ? (
                    <>
                      <Heading
                        as="h1"
                        className="mb-3 mt-0 whitespace-pre-line text-center md:text-left"
                      >
                        {`Fishbrain Pro 
                        A smarter way to fish`}
                      </Heading>
                    </>
                  ) : (
                    <>
                      <Heading
                        as="h1"
                        className="mb-3 mt-0 whitespace-pre-line text-center md:text-left"
                      >
                        {`Try Fishbrain Pro 
                        ${FREE_TRIAL_DAYS} days for free`}
                      </Heading>
                    </>
                  )}
                </>
              )}

              <ProFeaturesList className="my-3 w-max sm:mx-auto sm:pr-14 md:mx-0" />
            </div>

            <div
              style={{ backgroundImage: `url(${wave1.src})` }}
              className="-mb-1 block h-6 md:hidden"
            />

            <ProPaymentPlans
              className="col-start-1 row-start-2 px-6"
              coupon={coupon}
              couponIsLoading={couponIsLoading}
              backgroundColour="bg-sea-foam"
              submitLink={submitLink}
              onSubmitLinkClick={handleSubmitLinkClick}
              proPlanId={proPlanId}
              onPlanSelect={setProPlanId}
              userHasBeenPro={userHasBeenPro}
            />
          </header>
          <ProSections />
          {isMobileDevice && distanceFromTop > (screenHeight / 3) * 2 && (
            <StickyBannerGetPro
              selectedPlanId={proPlanId}
              coupon={coupon}
              couponIsLoading={couponIsLoading}
              submitLink={submitLink}
            />
          )}
        </>
      )}
    </>
  );
};
